// AccountMenu.jsx
// Top-right account control. Three states — but ALWAYS shows a profile icon.
//   1. unknown    → checking session token validity (renders nothing — short flash)
//   2. logged out → profile-silhouette icon → click → dropdown ["Sign in", "Settings"]
//   3. logged in  → circle with email initial → click → dropdown [email, "Settings", "Sign out"]
//
// 2026-05-30 (Braeden): replaced the old "Sign in" pill with a consistent
// profile icon in both states. One icon at the top-right, click for options.
//
// Session token persisted as `aria_session_token` in localStorage. On sign-out
// we just clear the token and flip state; no server-side revocation needed
// (the token's HMAC-signed TTL expires on its own).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PlanColumns } from './planCatalog';
import { getApiBase, getAriaUserIdForApi } from './apiBase';
import PaymentSheet from './PaymentSheet';

const TOKEN_KEY = 'aria_session_token';
const EMAIL_KEY = 'aria_session_email';

function readStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch (_) { return ''; }
}
function readStoredEmail() {
  try { return localStorage.getItem(EMAIL_KEY) || ''; } catch (_) { return ''; }
}
function writeSession(token, email) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token); else localStorage.removeItem(TOKEN_KEY);
    if (email) localStorage.setItem(EMAIL_KEY, email); else localStorage.removeItem(EMAIL_KEY);
  } catch (_) {}
}

async function fetchMe(token) {
  const base = (getApiBase() || '').replace(/\/$/, '');
  try {
    const r = await fetch(`${base}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // 401/403 = real auth failure (bad/expired token) → caller wipes session.
    // 4xx/5xx = server problem, treat as transient.
    if (r.status === 401 || r.status === 403) return { invalidToken: true };
    if (!r.ok) return { networkError: true };
    return await r.json();
  } catch (_) {
    // Network unreachable (CORS, offline, DNS, EHOSTDOWN). Don't sign the
    // user out — backend may just be temporarily down (PC asleep, etc.).
    return { networkError: true };
  }
}

// Desktop starts at account sign-in. Do not mount voice/Assist effects until
// the backend verifies a real account session (a guest token is insufficient).
export function DesktopAccountGate({ children }) {
  const desktop = typeof window !== 'undefined' && !!window.electronAPI;
  const [status, setStatus] = useState(desktop ? 'checking' : 'ready');
  const [showForm, setShowForm] = useState(true);
  const verifiedToken = useRef('');
  const check = useCallback(async () => {
    if (!desktop) return;
    const token = readStoredToken();
    if (!token) { verifiedToken.current = ''; setStatus('signin'); return; }
    const result = await fetchMe(token);
    if (readStoredToken() !== token) return;
    if (result?.userId && result.authMethod === 'session') {
      verifiedToken.current = token;
      setStatus('ready');
    } else if (result?.networkError) {
      if (verifiedToken.current !== token) setStatus('offline');
    } else {
      verifiedToken.current = '';
      setStatus('signin');
    }
  }, [desktop]);
  useEffect(() => {
    if (!desktop) return;
    check();
    const timer = setInterval(check, 5000);
    window.addEventListener('aria-account-id', check);
    window.addEventListener('storage', check);
    return () => { clearInterval(timer); window.removeEventListener('aria-account-id', check); window.removeEventListener('storage', check); };
  }, [check, desktop]);
  if (!desktop || status === 'ready') return children;
  const signedIn = async (token, email) => {
    writeSession(token, email);
    const account = await fetchMe(token);
    if (account?.userId && account.authMethod === 'session') {
      await adoptAccountId(account.userId, token);
    }
    await check();
  };
  return <>
    <div className="authgate" style={{ zIndex: 100 }} role="dialog" aria-modal="true" aria-label="Sign in to Aria">
      <div className="authgate__card">
        <div className="authgate__brand">Aria</div>
        <h2>{status === 'checking' ? 'Checking your account…' : status === 'offline' ? 'Cannot verify your account' : 'Sign in to Aria'}</h2>
        <p>{status === 'offline' ? 'Check your connection, then retry. Your saved sign-in has not been removed.' : 'Use the account you used to download Aria.'}</p>
        {status === 'offline' ? <button className="authgate__btn" onClick={check}>Retry</button> : status === 'signin' ? <button className="authgate__btn" onClick={() => setShowForm(true)}>Sign in</button> : null}
      </div>
    </div>
    {status === 'signin' && showForm && <AuthModal initialMode="signin" onClose={() => setShowForm(false)} onSuccess={signedIn} />}
  </>;
}

// ── AuthGate — blocks HowTo until the account is READY ──────────────────────
// Two hard requirements (owner rule):
//   1. Email must be VERIFIED before the app can be used.
//   2. A PAID plan must have payment on file (free needs nothing).
// Shows a full-screen overlay while either is unmet; polls until cleared.
// Only applies to signed-in accounts — anonymous/guest usage is untouched.
export function AuthGate() {
  const [me, setMe] = useState(undefined); // undefined=loading, null=no gate
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [switching, setSwitching] = useState(false);

  const check = React.useCallback(async () => {
    const token = readStoredToken();
    if (!token) { setMe(null); return; }
    const r = await fetchMe(token);
    // Only gate when we KNOW the state (real account) — never lock out on a
    // transient network error or an invalid/expired token.
    if (r && r.userId && r.authMethod === 'session') setMe(r);
    else setMe(null);
  }, []);

  useEffect(() => {
    check();
    const onEvt = () => check();
    window.addEventListener('aria-account-id', onEvt);
    window.addEventListener('aria-billing-identity', onEvt);
    const iv = setInterval(check, 5000); // detect verification / payment quickly
    return () => { clearInterval(iv); window.removeEventListener('aria-account-id', onEvt); window.removeEventListener('aria-billing-identity', onEvt); };
  }, [check]);

  if (me === undefined || me === null) return null;
  const emailGate = me.emailVerified === false;
  const payGate = !emailGate && me.needsPayment === true;
  if (!emailGate && !payGate) return null;

  const resend = async () => {
    setResending(true);
    try {
      const base = (getApiBase() || '').replace(/\/$/, '');
      await fetch(`${base}/api/auth/resend-verification`, { method: 'POST', headers: { Authorization: `Bearer ${readStoredToken()}` } });
      setResent(true);
    } catch (_) {}
    setResending(false);
  };
  const goPay = () => {
    // GlobalCheckout (always mounted at the app root) catches this and opens the
    // payment popup OVER the gate — there is no separate subscription page.
    try { localStorage.setItem('aria_intent_subscribe', me.plan === 'ultra' ? 'ultra' : '1'); } catch (_) {}
    try { window.dispatchEvent(new CustomEvent('aria-open-checkout', { detail: { plan: me.plan === 'ultra' ? 'ultra' : 'subscription' } })); } catch (_) {}
  };
  // Escape hatch — the gate blocks the whole app (including Settings), so a user
  // who picked a paid plan but doesn't want to pay would otherwise be stuck.
  // Switch them to Free right here; the next /api/auth/me poll clears the gate.
  const switchToFree = async () => {
    setSwitching(true);
    try {
      const base = (getApiBase() || '').replace(/\/$/, '');
      await fetch(`${base}/api/auth/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${readStoredToken()}` },
        body: JSON.stringify({ plan: 'free' }),
      });
      await check(); // re-poll: needsPayment → false → gate dismisses itself
    } catch (_) {}
    setSwitching(false);
  };

  return createPortal(
    <div className="authgate">
      <div className="authgate__card">
        <div className="authgate__brand">HowTo</div>
        {emailGate ? (
          <>
            <div className="authgate__icon authgate__icon--mail">✉</div>
            <h2>Verify your email to start</h2>
            <p>We sent a confirmation link to <strong>{me.email || 'your email'}</strong>. Click it, and HowTo unlocks automatically — this page will update on its own.</p>
            <button type="button" className="authgate__btn" onClick={resend} disabled={resending || resent}>
              {resent ? 'Email sent ✓' : resending ? 'Sending…' : 'Resend email'}
            </button>
            <p className="authgate__hint">Wrong address or didn't get it? Check spam, or resend above.</p>
          </>
        ) : (
          <>
            <div className="authgate__icon authgate__icon--card">💳</div>
            <h2>Add a payment method to continue</h2>
            {/* Say the TRACKED plan, never a default (8/13). `isUltra ? Ultra
                : Pro` labelled a $200/mo customer as the $20 plan because
                anything not exactly 'ultra' fell to Pro. me.billing comes from
                Stripe with the real tier and price. */}
            <p>You picked <strong>{(me.billing && me.billing.label) || `HowTo ${me.plan === 'ultra' || me.plan === 'ultra_200' ? 'Ultra' : 'Pro'}`}</strong>
              {me.billing && me.billing.monthly ? ` ($${me.billing.monthly}/month)` : ''}. Add your card or bank to activate it — or switch to the free plan below.</p>
            <button type="button" className="authgate__btn" onClick={goPay}>Add payment</button>
            <button type="button" className="authgate__link" onClick={switchToFree} disabled={switching}>
              {switching ? 'Switching…' : 'Use the free plan instead'}
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

// UNIVERSAL MEMORY: make the signed-in ACCOUNT id the one that scopes memory.
// Writes it into `aria_user_id` (the key every conversation/memory request
// uses) so the same account → same memory on any device, and fires a ONE-TIME
// merge of this device's pre-login anonymous memory into the account (so nothing
// the user did before signing in is lost). Idempotent via a per-account marker.
async function adoptAccountId(accountUserId, token) {
  if (!accountUserId) return;
  try {
    const prevAnon = localStorage.getItem('aria_user_id') || '';
    localStorage.setItem('aria_user_id', accountUserId);
    const mergedKey = 'aria_merged_' + accountUserId;
    if (token && prevAnon && prevAnon !== accountUserId && !localStorage.getItem(mergedKey)) {
      const base = (getApiBase() || '').replace(/\/$/, '');
      try {
        await fetch(`${base}/api/auth/merge-identity`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ fromUserId: prevAnon }),
        });
        localStorage.setItem(mergedKey, '1');
        // let the app re-key its in-memory userId + realtime sync to the account
        try { window.dispatchEvent(new CustomEvent('aria-account-id', { detail: { userId: accountUserId } })); } catch (_) {}
      } catch (_) {}
    } else {
      try { window.dispatchEvent(new CustomEvent('aria-account-id', { detail: { userId: accountUserId } })); } catch (_) {}
    }
  } catch (_) {}
}

// First name only — the user may enter "First Last"; greet by the first word.
function firstNameFrom(name) {
  return String(name || '').trim().split(/\s+/)[0] || '';
}

// Birthday parts are TYPEABLE — accept free text and normalize to YYYY-MM-DD
// pieces. Month accepts a number OR a name ("apr", "April" → "04").
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
function normBDay(v) { const n = parseInt(String(v).replace(/\D/g, ''), 10); return (n >= 1 && n <= 31) ? String(n).padStart(2, '0') : ''; }
function normBMonth(v) {
  const s = String(v).trim().toLowerCase();
  if (!s) return '';
  if (/^\d{1,2}$/.test(s)) { const n = parseInt(s, 10); return (n >= 1 && n <= 12) ? String(n).padStart(2, '0') : ''; }
  const i = MONTH_NAMES.findIndex((m) => m.toLowerCase().startsWith(s));
  return i >= 0 ? String(i + 1).padStart(2, '0') : '';
}
function normBYear(v) { const n = parseInt(String(v).replace(/\D/g, ''), 10); const yr = new Date().getFullYear(); return (n >= 1900 && n <= yr) ? String(n) : ''; }

// TIER AVATARS (Braeden 8/13). Paying members get their tier's badge instead
// of a plain initial. The file is dropped in shimmer-web/public/avatars/ and
// picked up automatically — if it isn't there, the initial still renders, so
// a missing asset can never leave someone with a broken avatar.
const TIER_AVATAR = {
  free: '/avatars/tier-free.png',           // purple shield "F"
  basic_20: '/avatars/tier-basic-20.png',   // the $20/mo standard badge
  ultra_200: '/avatars/tier-ultra-200.png', // crown — $200/mo top tier
};
function tierAvatarSrc(plan) {
  const p = String(plan || '').toLowerCase();
  if (TIER_AVATAR[p]) return TIER_AVATAR[p];
  if (p === 'ultra') return TIER_AVATAR.ultra_200;
  if (p === 'basic' || p === 'pro' || p === 'subscription') return TIER_AVATAR.basic_20;
  // Signed in with no paid plan IS the free tier — it gets a badge too, so
  // every member has one (8/13). Unknown/absent plan keeps the initial.
  if (p === 'none' || p === 'trial') return TIER_AVATAR.free;
  return null;
}

// headless (8/22): the profile pill left the header for the dashboard's
// Account panel, which dispatches 'aria-open-signin' - but the only listener
// lived in this component, which was no longer mounted anywhere. "Sign in"
// was a dead button, measured in the served bundle. Mounted headless from
// App, this renders the sign-in modal and nothing else.
export default function AccountMenu({ headless = false } = {}) {
  const [state, setState] = useState('unknown'); // 'unknown' | 'out' | 'in'
  const [plan, setPlan] = useState(() => { try { return localStorage.getItem('aria_user_plan') || ''; } catch (_) { return ''; } });
  const [avatarBroken, setAvatarBroken] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState(() => { try { return localStorage.getItem('aria_user_first') || ''; } catch (_) { return ''; } });
  const [menuOpen, setMenuOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('signin'); // which card the modal opens on (aria-open-signin detail.mode)
  const [supportOpen, setSupportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const wrapRef = useRef(null);

  const openHistory = () => {
    setMenuOpen(false);
    setHistoryOpen(true);
  };

  // Open the support chat panel — Aria in "support agent" mode. This is
  // separate from the main HowTo conversation surface (which is the "how-to"
  // text). Use this when the user needs help with HowTo itself.
  const openSupport = () => {
    setMenuOpen(false);
    setSupportOpen(true);
  };

  // On mount: validate any stored token. Invalid/expired → clear + show signed-out.
  useEffect(() => {
    const token = readStoredToken();
    if (!token) { setState('out'); return; }
    (async () => {
      const me = await fetchMe(token);
      if (me && me.userId) {
        setState('in');
        setEmail(readStoredEmail());
        // Remember the tier so the avatar badge survives a reload before
        // /api/auth/me answers (8/13).
        // Prefer the Stripe-tracked tier over the stored plan field — the
        // stored one read 'free' for a paying customer (8/13).
        const realTier = (me.billing && me.billing.tier) || me.plan;
        if (realTier) {
          setPlan(realTier);
          try { localStorage.setItem('aria_user_plan', String(realTier)); } catch (_) {}
        }
        if (me.displayName) {
          const fn = firstNameFrom(me.displayName);
          setFirstName(fn);
          try { localStorage.setItem('aria_user_first', fn); } catch (_) {}
        }
        // Self-heal: a returning signed-in browser may still be scoped to an old
        // anon id — re-point it at the account id (+ merge once).
        adoptAccountId(me.userId, token);
      } else if (me && me.invalidToken) {
        // Real auth failure — wipe session.
        writeSession('', '');
        setState('out');
      } else {
        // Network error or transient server problem. Trust the stored token
        // optimistically so the user isn't kicked out when the backend is
        // briefly unreachable. Real revalidation happens on next request.
        setState('in');
        setEmail(readStoredEmail());
      }
    })();
  }, []);

  // Click-outside closes the dropdown.
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  // External trigger: out-of-credits modal asks us to open the sign-in modal.
  useEffect(() => {
    // `detail.mode: 'signup'` lands a first-timer on the create-account card
    // (the intro's stage three opens it that way, 9/5); default stays sign-in.
    const onOpen = (e) => {
      if (state === 'in') return;
      try { setModalMode(e && e.detail && e.detail.mode === 'signup' ? 'signup' : 'signin'); } catch (_) {}
      setModalOpen(true);
    };
    window.addEventListener('aria-open-signin', onOpen);
    return () => window.removeEventListener('aria-open-signin', onOpen);
  }, [state]);

  const handleSignOut = useCallback(() => {
    writeSession('', '');
    // Also wipe any persisted conversation so the next view is a clean slate.
    // (Anonymous mode no longer persists, but old keys from a prior signed-in
    // session can still be lying around — clear them now.)
    try {
      localStorage.removeItem('aria_session_messages');
      localStorage.removeItem('aria_session_history');
      // Drop the account-scoped id so the next (anonymous) user gets a fresh
      // anon id and does NOT read/write the signed-out account's memory.
      localStorage.removeItem('aria_user_id');
      localStorage.removeItem('aria_user_email');
      localStorage.removeItem('aria_user_first');
    } catch (_) {}
    setState('out');
    setEmail('');
    setFirstName('');
    setMenuOpen(false);
    // Force a reload so React state for messages/history flushes too.
    try { window.location.reload(); } catch (_) {}
  }, []);

  const handleAuthSuccess = useCallback(async (token, userEmail) => {
    writeSession(token, userEmail);
    setEmail(userEmail);
    setState('in');
    setModalOpen(false);
    // Mirror the account email into the BILLING identity key the Subscription
    // view + Stripe checkout read (`aria_user_email`). Without this a freshly
    // signed-up user had a userId but no email, so the checkout buttons never
    // rendered and they literally could not subscribe.
    try { if (userEmail) localStorage.setItem('aria_user_email', userEmail); } catch (_) {}
    // Adopt the account id (+ one-time merge of this device's anon memory) and
    // pick up the first name so the menu greets by name, not email.
    try {
      const me = await fetchMe(token);
      if (me && me.userId) await adoptAccountId(me.userId, token);
      if (me && me.displayName) {
        const fn = firstNameFrom(me.displayName);
        setFirstName(fn);
        try { localStorage.setItem('aria_user_first', fn); } catch (_) {}
      }
    } catch (_) {}
    // Let an already-open Subscription view pick up the new billing identity
    // immediately (e.g. user signed in from the out-of-funds modal).
    try { window.dispatchEvent(new CustomEvent('aria-billing-identity', { detail: { email: userEmail } })); } catch (_) {}
  }, []);

  if (state === 'unknown') return null;

  // ── Profile icon SVG (person silhouette) — same in both signed-in/out states.
  // Logged-in users see their initial INSIDE the circle instead of the silhouette.
  const ProfileIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  );

  // Open the Settings surface. Hash-routed like everything else in the app.
  const openSettings = () => {
    setMenuOpen(false);
    try { window.location.hash = 'settings'; } catch (_) {}
  };

  if (headless) {
    return (
      <>
        {state !== 'in' && modalOpen && (
          <AuthModal
            initialMode={modalMode}
            onClose={() => setModalOpen(false)}
            onSuccess={handleAuthSuccess}
          />
        )}
      </>
    );
  }

  // ── LOGGED OUT — profile icon → dropdown [Sign in, Settings] ──────────────
  if (state === 'out') {
    return (
      <>
        <div className="account-wrap" ref={wrapRef}>
          <button
            type="button"
            className="account-avatar account-avatar--out"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Account menu"
            aria-haspopup="true"
            aria-expanded={menuOpen}
          >
            <ProfileIcon />
          </button>
          {menuOpen && (
            <div className="account-menu" role="menu">
              <button
                type="button"
                className="account-menu__item"
                onClick={() => { setMenuOpen(false); setModalOpen(true); }}
                role="menuitem"
              >
                Sign in
              </button>
              <button
                type="button"
                className="account-menu__item"
                onClick={openSettings}
                role="menuitem"
              >
                Settings
              </button>
              <button
                type="button"
                className="account-menu__item"
                onClick={openHistory}
                role="menuitem"
              >
                History
              </button>
              <button
                type="button"
                className="account-menu__item"
                onClick={openSupport}
                role="menuitem"
              >
                Support
              </button>
            </div>
          )}
        </div>
        {modalOpen && (
          <AuthModal
            initialMode={modalMode}
            onClose={() => setModalOpen(false)}
            onSuccess={handleAuthSuccess}
          />
        )}
        {supportOpen && (
          <SupportChat onClose={() => setSupportOpen(false)} />
        )}
        {historyOpen && (
          <HistoryPanel onClose={() => setHistoryOpen(false)} />
        )}
      </>
    );
  }

  // ── LOGGED IN — initial-circle avatar → dropdown [name, Settings, Sign out] ─
  const initial = (firstName || email || 'A').trim().charAt(0).toUpperCase() || 'A';
  const tierSrc = tierAvatarSrc(plan);
  return (
    <div className="account-wrap" ref={wrapRef}>
      <button
        type="button"
        className="account-avatar"
        onClick={() => setMenuOpen((v) => !v)}
        aria-label="Account menu"
        aria-haspopup="true"
        aria-expanded={menuOpen}
        style={tierSrc && !avatarBroken ? { padding: 0, overflow: 'hidden' } : undefined}
      >
        {tierSrc && !avatarBroken ? (
          <img src={tierSrc} alt="" onError={() => setAvatarBroken(true)}
               style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', display: 'block' }} />
        ) : initial}
      </button>
      {menuOpen && (
        <div className="account-menu" role="menu">
          <div className="account-menu__email" title={email}>{firstName || email || 'Signed in'}</div>
          <button
            type="button"
            className="account-menu__item"
            onClick={openSettings}
            role="menuitem"
          >
            Settings
          </button>
          <button
            type="button"
            className="account-menu__item"
            onClick={handleSignOut}
            role="menuitem"
          >
            Sign out
          </button>
          <button
            type="button"
            className="account-menu__item"
            onClick={openHistory}
            role="menuitem"
          >
            History
          </button>
          <button
            type="button"
            className="account-menu__item"
            onClick={openSupport}
            role="menuitem"
          >
            Support
          </button>
        </div>
      )}
      {supportOpen && (
        <SupportChat onClose={() => setSupportOpen(false)} />
      )}
      {historyOpen && (
        <HistoryPanel onClose={() => setHistoryOpen(false)} />
      )}
    </div>
  );
}

// ── History — clean day list, Aria-titled ─────────────────────
// Every day you talked to Aria gets a row: the DATE is the way in, the title
// is Aria's one-liner of the important thing discussed. Click a day → the
// full conversation (user bubbles; Aria replies as plain text, per the chat
// style rule). Titles generate lazily server-side on the local model.
function HistoryPanel({ onClose }) {
  const [days, setDays] = useState(null);      // null = loading
  const [err, setErr] = useState('');
  const [openDay, setOpenDay] = useState(null); // { date, title }
  const [turns, setTurns] = useState(null);

  const userId = getAriaUserIdForApi() || '';
  const base = (getApiBase() || '').replace(/\/$/, '');

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await fetch(`${base}/api/conversations/days?userId=${encodeURIComponent(userId)}`);
        const d = await r.json();
        if (dead) return;
        if (!d.ok) throw new Error(d.error || 'load failed');
        setDays(d.days || []);
      } catch (e) { if (!dead) { setErr('Could not load history.'); setDays([]); } }
    })();
    return () => { dead = true; };
  }, [base, userId]);

  const openTranscript = async (day) => {
    setOpenDay(day); setTurns(null);
    try {
      const r = await fetch(`${base}/api/conversations/day?userId=${encodeURIComponent(userId)}&date=${day.date}`);
      const d = await r.json();
      setTurns(d.ok ? (d.turns || []) : []);
    } catch (_) { setTurns([]); }
  };

  const fmtDate = (iso) => {
    try {
      const [y, m, dd] = iso.split('-').map(Number);
      return new Date(y, m - 1, dd).toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' });
    } catch (_) { return iso; }
  };
  const fmtTime = (ts) => {
    try { return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); } catch (_) { return ''; }
  };

  return createPortal(
    <div className="account-modal__backdrop" onClick={onClose}>
      <div className="history-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="History">
        <button type="button" className="account-modal__close" onClick={onClose} aria-label="Close">×</button>

        {!openDay ? (
          <>
            <h2 className="history-panel__title">History</h2>
            <p className="history-panel__sub">Every conversation with Aria, by day. Tap a date to read it back.</p>
            {days === null && <p className="history-panel__empty">Loading…</p>}
            {err && <p className="history-panel__empty">{err}</p>}
            {days !== null && !err && days.length === 0 && (
              <p className="history-panel__empty">No conversations yet — they'll show up here after you talk with Aria.</p>
            )}
            <div className="history-panel__list">
              {(days || []).map((d) => (
                <button key={d.date} type="button" className="history-day" onClick={() => openTranscript(d)}>
                  <span className="history-day__date">{fmtDate(d.date)}</span>
                  <span className="history-day__title">{d.title || '…'}</span>
                  <span className="history-day__count">{d.turnCount}</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <button type="button" className="history-panel__back" onClick={() => { setOpenDay(null); setTurns(null); }}>
              ← All days
            </button>
            <h2 className="history-panel__title">{openDay.title || fmtDate(openDay.date)}</h2>
            <p className="history-panel__sub">{fmtDate(openDay.date)}</p>
            <div className="history-panel__scroll">
              {turns === null && <p className="history-panel__empty">Loading…</p>}
              {turns !== null && turns.length === 0 && <p className="history-panel__empty">Nothing recorded for this day.</p>}
              {(turns || []).map((t, i) => (
                <div key={i} className="history-turn">
                  <div className="history-turn__user">
                    <span className="history-turn__bubble">{t.prompt}</span>
                    <span className="history-turn__time">{fmtTime(t.ts)}</span>
                  </div>
                  {t.reply && <div className="history-turn__aria">{t.reply}</div>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

// ── Support chat — Aria, but in support-agent mode ─────────────────────────
// Pops up a small chat panel where the user can ask Aria for help WITH HowTo
// itself (billing, how to use a workbench, etc). Separate channel from the
// main home "how-to" conversation so the contexts don't get mixed.
// Identical mic glyph to the other Aria popups (AriaDock / SetupHelpBox).
const SupportMicIcon = ({ on }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={on ? 2.6 : 2.2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 10a7 7 0 0 0 14 0M12 19v3" />
  </svg>
);

function SupportChat({ onClose }) {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hey — I'm Aria, your HowTo support. Ask me anything about your account, billing, or how something works.",
    },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  // ── Draggable position ────────────────────────────────────────────────
  // The panel floats on top of the app and can be dragged by its header.
  // Position is saved per-user in localStorage so it stays where they left
  // it across sessions. Default: bottom-right corner.
  const PANEL_W = 380;
  const PANEL_H = 560;
  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('aria_support_pos') || 'null');
      if (saved && typeof saved.left === 'number' && typeof saved.top === 'number') return saved;
    } catch (_) {}
    // Default = bottom-right with 24px margin
    const left = (typeof window !== 'undefined' ? window.innerWidth : 1200) - PANEL_W - 24;
    const top  = (typeof window !== 'undefined' ? window.innerHeight : 800) - PANEL_H - 24;
    return { left: Math.max(16, left), top: Math.max(16, top) };
  });
  const dragRef = useRef(null);  // { startX, startY, panelL, panelT }

  const onHeaderMouseDown = (e) => {
    // Ignore clicks on the close button — those should fire its onClick.
    if (e.target.closest('.support-chat__close')) return;
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      panelL: pos.left,
      panelT: pos.top,
    };
    const onMove = (ev) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      const nextL = Math.max(0, Math.min(window.innerWidth - 60, dragRef.current.panelL + dx));
      const nextT = Math.max(0, Math.min(window.innerHeight - 60, dragRef.current.panelT + dy));
      setPos({ left: nextL, top: nextT });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      // Persist the final position so it sticks across reloads.
      try {
        localStorage.setItem('aria_support_pos', JSON.stringify({
          left: dragRef.current ? Math.max(0, Math.min(window.innerWidth - 60, (dragRef.current.panelL + (window.event?.clientX - dragRef.current.startX)) || pos.left)) : pos.left,
          top: pos.top,
        }));
      } catch (_) {}
      dragRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Save final position on unmount or whenever pos settles.
  useEffect(() => {
    try { localStorage.setItem('aria_support_pos', JSON.stringify(pos)); } catch (_) {}
  }, [pos]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async (e, override) => {
    e?.preventDefault();
    const text = String(override != null ? override : input).trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setInput('');
    setBusy(true);
    try {
      const base = (getApiBase() || '').replace(/\/$/, '');
      const token = readStoredToken();
      const r = await fetch(`${base}/api/ai/conversation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'X-Aria-Surface': 'support',
        },
        body: JSON.stringify({
          prompt: text,
          initialQuery: text,
          conversationHistory: messages.map((m) => ({ role: m.role, content: m.content })),
          language: 'en',
          supportMode: true,
        }),
      });
      const data = await r.json().catch(() => ({}));
      const reply = data?.answer || data?.reply || data?.content || "I'm having trouble reaching support right now — try again in a second.";
      setMessages((m) => [...m, { role: 'assistant', content: String(reply).trim() }]);
    } catch (_) {
      setMessages((m) => [...m, { role: 'assistant', content: "Network hiccup — try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  // ── Mic: record → transcribe → send (same one-shot flow as the other popups) ──
  const transcribe = async (blob, mime) => {
    const base64 = await new Promise((resolve, reject) => { const r = new FileReader(); r.onloadend = () => resolve(String(r.result || '').split(',')[1] || ''); r.onerror = () => reject(new Error('read')); r.readAsDataURL(blob); });
    if (!base64) return '';
    let uid = ''; try { uid = localStorage.getItem('aria_user_id') || localStorage.getItem('howto_owner_id') || ''; } catch (_) {}
    const billing = uid ? { userId: uid } : {};
    if (typeof window !== 'undefined' && window.electronAPI?.transcribeAudio) {
      const ipc = await window.electronAPI.transcribeAudio({ audio: base64, mime, ...billing });
      const data = (ipc && ipc.data) || {};
      return (ipc && ipc.ok && data.text) ? String(data.text).trim() : '';
    }
    const base = String(getApiBase() || '').replace(/\/$/, '');
    const r = await fetch(`${base}/api/speech/transcribe`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ audio: base64, mime, ...billing }) });
    const data = await r.json().catch(() => ({}));
    return (r.ok && data && data.text) ? String(data.text).trim() : '';
  };
  const toggleMic = async () => {
    if (recording) { try { mediaRef.current && mediaRef.current.stop(); } catch (_) {} return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const supported = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported;
      const mime = supported && MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        try { (streamRef.current?.getTracks() || []).forEach((t) => t.stop()); } catch (_) {}
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        if (!blob.size) return;
        try { const text = await transcribe(blob, mr.mimeType || 'audio/webm'); if (text) await send(null, text); } catch (_) {}
      };
      mr.start(); mediaRef.current = mr; setRecording(true);
    } catch (_) { /* mic unavailable — typing still works */ }
  };
  useEffect(() => () => { try { (streamRef.current?.getTracks() || []).forEach((t) => t.stop()); } catch (_) {} }, []);

  // Portal directly to document.body so we escape every parent's stacking
  // context and the panel can float over the app while leaving the rest of
  // the UI fully interactive. No backdrop, no click-to-dismiss — only the
  // X closes it. Header is the drag handle.
  return createPortal(
    <div
      className="support-chat"
      role="dialog"
      aria-label="HowTo Support"
      style={{ left: `${pos.left}px`, top: `${pos.top}px`, right: 'auto', bottom: 'auto' }}
    >
      <div
        className="support-chat__header support-chat__header--centered"
        onMouseDown={onHeaderMouseDown}
        style={{ cursor: 'grab', userSelect: 'none' }}
      >
        <div className="support-chat__script">Aria</div>
        <button type="button" className="support-chat__close" onClick={onClose} aria-label="Close">×</button>
      </div>
        <div className="support-chat__body" ref={scrollRef}>
          {messages.map((m, i) => (
            <div key={i} className={`support-chat__bubble support-chat__bubble--${m.role}`}>
              {m.content}
            </div>
          ))}
          {busy && (
            <div className="support-chat__bubble support-chat__bubble--assistant support-chat__typing">
              <span/><span/><span/>
            </div>
          )}
        </div>
        <form className="support-chat__input-row" onSubmit={send}>
          <button
            type="button"
            onClick={() => { try { inputRef.current && inputRef.current.focus(); } catch (_) {} }}
            aria-label="Assist — let Aria use your screen"
            title="Assist — let Aria help on your screen"
            style={{ flex: 'none', width: 38, height: 38, borderRadius: 999, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-2,#888)', background: 'rgba(127,127,127,0.16)' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          </button>
          <button
            type="button"
            onClick={toggleMic}
            aria-label={recording ? 'Stop recording' : 'Talk to Aria'}
            title={recording ? 'Listening… tap to stop' : 'Talk to Aria'}
            style={{ flex: 'none', width: 38, height: 38, borderRadius: 999, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: recording ? '#fff' : 'var(--text-2,#888)', background: recording ? '#ff453a' : 'rgba(127,127,127,0.16)', boxShadow: recording ? '0 0 0 4px rgba(255,69,58,0.25)' : 'none', transition: 'background .15s' }}
          >
            <SupportMicIcon on={recording} />
          </button>
          <input
            ref={inputRef}
            type="text"
            placeholder={recording ? 'Listening…' : 'Ask Aria, or tap the mic to talk…'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            autoFocus
          />
          <button type="submit" disabled={!input.trim() || busy} aria-label="Send">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
          </button>
        </form>
    </div>,
    document.body
  );
}

// ── Saved sign-in (9/30) ─────────────────────────────────────────────
// Braeden: "the sign in screen needs to have a save button for the info so it saves the users info
// when they sign in so they can click a button and all of their password and email stay saved."
// Desktop app: the pair is encrypted by the OS through main.js (window.electronAPI.savedLogin).
// Website: it sits in this browser's localStorage, which is only as private as the browser
// profile, so the box starts unticked there. An app build from before this change has no
// savedLogin bridge yet; then the desktop also uses localStorage, and the first read after the
// app is relaunched moves that copy into the encrypted store and removes it.
const SAVED_LOGIN_KEY = 'aria_saved_login';
async function readSavedLogin(desktop) {
  try {
    const bridge = desktop && window.electronAPI && window.electronAPI.savedLogin;
    const raw = localStorage.getItem(SAVED_LOGIN_KEY);
    const local = raw ? JSON.parse(raw) : null;
    if (bridge) {
      const enc = await bridge.read();
      if (enc && enc.email) return enc;
      if (local && local.email && local.password) {
        const r = await bridge.save(local);
        if (r && r.ok) { localStorage.removeItem(SAVED_LOGIN_KEY); return local; }
      }
      return null;
    }
    return local && local.email && local.password ? local : null;
  } catch (_) { return null; }
}
async function writeSavedLogin(desktop, login) {
  try {
    const bridge = desktop && window.electronAPI && window.electronAPI.savedLogin;
    if (bridge) return await bridge.save(login);
    localStorage.setItem(SAVED_LOGIN_KEY, JSON.stringify({ email: login.email, password: login.password }));
    return { ok: true };
  } catch (e) { return { ok: false, error: e && e.message }; }
}
async function clearSavedLogin(desktop) {
  try {
    const bridge = desktop && window.electronAPI && window.electronAPI.savedLogin;
    if (bridge) await bridge.clear();
    localStorage.removeItem(SAVED_LOGIN_KEY);
  } catch (_) {}
}

// ── Sign in / sign up modal ─────────────────────────────────────────────────
// Two visually distinct modes:
//   signin: compact welcome-back card, 2 fields, blue accent
//   signup: warmer welcome card, same 2 fields (email + password), yellow
//           Aria accent — a verify link goes to the email after signup.
function AuthModal({ onClose, onSuccess, initialMode = 'signin' }) {
  // Account creation is WEB ONLY — the desktop app is a members-only perk, so
  // in Electron we only allow SIGN IN and point new users to the website.
  const isDesktopApp = typeof window !== 'undefined' && !!window.electronAPI;
  const [mode, setMode] = useState(isDesktopApp ? 'signin' : (initialMode === 'signup' ? 'signup' : 'signin')); // 'signin' | 'signup'
  // Signup is a 5-step wizard:
  //   form → confirm (email code) → plan → payment (paid plans only) → verifying
  const [step, setStep] = useState('form');
  const [nameInput, setNameInput] = useState('');
  // Birthday as three easy dropdowns (Month · Day · Year). Composed into the
  // YYYY-MM-DD `birthday` string the backend expects — no fiddly native
  // calendar that starts on today's date and makes you page back decades.
  const [bDay, setBDay] = useState('');
  const [bMonth, setBMonth] = useState('');
  const [bYear, setBYear] = useState('');
  const [birthday, setBirthday] = useState('');
  useEffect(() => {
    const d = normBDay(bDay), m = normBMonth(bMonth), y = normBYear(bYear);
    setBirthday(d && m && y ? `${y}-${m}-${d}` : '');
  }, [bDay, bMonth, bYear]);
  const [emailInput, setEmailInput] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // Saved sign-in (9/30): the box, the saved pair, and a one-line note about where it lives.
  const [saveLogin, setSaveLogin] = useState(isDesktopApp);
  const [savedLogin, setSavedLogin] = useState(null);   // { email, password } or null
  const [savedNote, setSavedNote] = useState('');
  useEffect(() => {
    let alive = true;
    (async () => {
      const s = await readSavedLogin(isDesktopApp);
      if (alive && s && s.email) { setSavedLogin(s); setEmailInput(s.email); setSaveLogin(true); }
    })();
    return () => { alive = false; };
  }, [isDesktopApp]);
  // Plan-choice state — only relevant after successful signup.
  const [allowOverage, setAllowOverage] = useState(true);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('pro'); // 'free' | 'pro' | 'ultra'
  const [overageConfirm, setOverageConfirm] = useState(false); // popup before buying w/ overage ON
  // Phone / SMS-consent step (sits between plan and payment). Collects the
  // user's mobile number + express consent to receive SERVICE texts
  // (transactional — never marketing). This is the A2P/TCPA opt-in record.
  const [phoneInput, setPhoneInput] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [phoneBusy, setPhoneBusy] = useState(false);
  // Stash the credentials we got back from signup so we can finalize the
  // login (call onSuccess) at the END of the wizard — not before.
  const pendingSessionRef = useRef(null);
  // ── Step 2 (confirm email) state ──
  const [codeInput, setCodeInput] = useState('');
  const [devCode, setDevCode] = useState('');     // surfaced only when the email couldn't be delivered
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [resendNote, setResendNote] = useState('');
  // ── Steps 4–5 (payment + verify) state ──
  const [payMeta, setPayMeta] = useState(null);   // { subscriptionId } from PaymentSheet
  const [verifyPhase, setVerifyPhase] = useState('checking'); // 'checking' | 'done' | 'failed'
  const [verifyNote, setVerifyNote] = useState('');
  const [verifyNonce, setVerifyNonce] = useState(0); // bump = re-run the activation poll

  // While the auth modal is mounted, dim the global yellow HowTo hero behind
  // it so it doesn't bleed through the backdrop. Cleaned up on close.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.add('aria-modal-open');
    return () => { document.body.classList.remove('aria-modal-open'); };
  }, []);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (!emailInput || !password) { setErr('Enter email and password.'); return; }
    if (mode === 'signup' && !nameInput.trim()) { setErr('Enter your name.'); return; }
    if (mode === 'signup' && !birthday) { setErr('Enter your birthday.'); return; }
    return performAuth(emailInput, password);
  }, [emailInput, password, nameInput, birthday, mode, performAuth]);

  // One click with the saved pair: fill the fields so the form shows what is happening, then go.
  const signInSaved = useCallback(async () => {
    if (!savedLogin || busy) return;
    setEmailInput(savedLogin.email); setPassword(savedLogin.password); setErr('');
    return performAuth(savedLogin.email, savedLogin.password);
  }, [savedLogin, busy, performAuth]);
  const forgetSaved = useCallback(async () => {
    await clearSavedLogin(isDesktopApp);
    setSavedLogin(null); setEmailInput(''); setPassword(''); setSavedNote('');
  }, [isDesktopApp]);

  async function performAuth(emailV, passwordV) {
    setBusy(true);
    setErr('');
    // Sign-in against a local backend is near-instant, which feels jarring — a
    // brief, deliberate beat (~1.5s) with a spinner reads as "signing you in".
    // Runs concurrently with the request, so it never ADDS more than the wait.
    const minWait = mode === 'signin' ? new Promise((res) => setTimeout(res, 1500)) : Promise.resolve();
    try {
      const base = (getApiBase() || '').replace(/\/$/, '');
      const url = mode === 'signup' ? `${base}/api/auth/signup` : `${base}/api/auth/session`;
      const body = mode === 'signup'
        ? { email: emailV, password: passwordV, name: nameInput.trim(), birthday }
        // bindIp:false — don't tie the session to an IP (matches signup); an IP
        // change (Wi-Fi ↔ cellular, Cloudflare edge) shouldn't sign you out.
        : { email: emailV, password: passwordV, bindIp: false };
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      // Signup returns { session: { token } }; sign-in (/api/auth/session)
      // returns { token } flat. Accept BOTH — the old code only read
      // data.session.token, so every sign-in looked like a failure.
      const token = data.session?.token || data.token;
      if (!r.ok || !token) {
        const msg = data.error || data.reason || `HTTP ${r.status}`;
        setErr(prettyErr(msg, mode));
        return;
      }
      // Sign in → close immediately. Sign up → confirm the email first, then
      // plan choice (free = data may train Aria, paid = private) → payment.
      if (mode === 'signup') {
        pendingSessionRef.current = { token, email: data.email || emailV, userId: data.userId || '' };
        setDevCode(data.verifyCode || '');
        setStep('confirm');
      } else {
        // Saved sign-in (9/30): keep the pair only after the server said yes to it.
        if (saveLogin) {
          const r = await writeSavedLogin(isDesktopApp, { email: emailV, password: passwordV });
          if (!(r && r.ok)) setSavedNote(`Couldn't save the login: ${(r && r.error) || 'unknown'}.`);
        } else {
          await clearSavedLogin(isDesktopApp);
        }
        await minWait;  // hold the spinner for the deliberate beat
        onSuccess(token, data.email || emailV);
      }
    } catch (e) {
      setErr('Network error. Try again.');
    } finally {
      setBusy(false);
    }
  }

  const isSignup = mode === 'signup';

  // Finalize the signup login — fires onSuccess with the stashed credentials.
  const finalizeSignup = useCallback(() => {
    const s = pendingSessionRef.current;
    if (!s) { onClose(); return; }
    // Persist the overage preference. The subscription/billing layer reads
    // this on first charge attempt. Off = hard-stop at credit limit.
    try { localStorage.setItem('aria_allow_overage', allowOverage ? '1' : '0'); } catch (_) {}
    onSuccess(s.token, s.email);
  }, [allowOverage, onClose, onSuccess]);

  // Record the plan + data-consent choice server-side (the consent record),
  // then route: paid plans collect payment INSIDE the wizard; free goes
  // straight to the setup beat. finalizeSignup() happens at the very end.
  const choosePlan = useCallback(async (plan) => {
    const s = pendingSessionRef.current;
    try {
      const base = (getApiBase() || '').replace(/\/$/, '');
      await fetch(`${base}/api/auth/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s?.token || ''}` },
        body: JSON.stringify({ plan }),
      });
    } catch (_) { /* consent re-recordable from Settings; never block signup */ }
    // Persist the extra-usage choice the user made on the plan screen so the
    // billing layer honors it on the first charge (on = metered overage).
    try { localStorage.setItem('aria_allow_overage', allowOverage ? '1' : '0'); } catch (_) {}
    // Next: the phone / SMS-consent step. The payment-or-verifying routing now
    // happens AFTER phone (submitPhone) so every new account records its SMS
    // opt-in before it lands.
    setStep('phone');
  }, [allowOverage]);

  // ── Phone / SMS-consent step handler — records the opt-in, then does the
  // plan routing choosePlan used to do (paid → payment, free → verifying).
  const submitPhone = useCallback(async ({ skip } = {}) => {
    const s = pendingSessionRef.current;
    const num = (phoneInput || '').replace(/[^\d+]/g, '');
    if (!skip) {
      if (num.replace(/\D/g, '').length < 10) { setErr('Enter a valid mobile number, or tap Skip for now.'); return; }
      if (!smsConsent) { setErr('Please check the box to agree to service texts, or tap Skip for now.'); return; }
    }
    setPhoneBusy(true);
    try {
      const base = (getApiBase() || '').replace(/\/$/, '');
      await fetch(`${base}/api/auth/sms-consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s?.token || ''}` },
        body: JSON.stringify({ phone: skip ? '' : num, consent: skip ? false : true }),
      });
    } catch (_) { /* consent re-recordable from Settings; never block signup */ }
    setPhoneBusy(false);
    setErr('');
    if (selectedPlan === 'pro' || selectedPlan === 'ultra') {
      setStep('payment');
    } else {
      setVerifyPhase('checking');
      setVerifyNote('');
      setStep('verifying');
    }
  }, [phoneInput, smsConsent, selectedPlan]);

  // ── Step 2: confirm email — the typed 6-digit code ─────────────────────────
  const submitCode = useCallback(async (e) => {
    if (e) e.preventDefault();
    const s = pendingSessionRef.current;
    const c = codeInput.replace(/\D/g, '');
    if (c.length !== 6) { setErr('Enter the 6-digit code from your email.'); return; }
    setConfirmBusy(true);
    setErr('');
    try {
      const base = (getApiBase() || '').replace(/\/$/, '');
      const r = await fetch(`${base}/api/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s?.token || ''}` },
        body: JSON.stringify({ code: c, email: s?.email }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) {
        setErr(d.error === 'code_expired' ? 'That code expired — hit Resend for a fresh one.'
          : d.error === 'code_locked' ? 'Too many tries. Wait a few minutes, then resend a fresh code.'
          : 'That code doesn’t match. Check the email and try again.');
        return;
      }
      setResendNote('');
      setStep('plan');
    } catch (_) {
      setErr('Network error. Try again.');
    } finally {
      setConfirmBusy(false);
    }
  }, [codeInput]);

  const resendCode = useCallback(async () => {
    const s = pendingSessionRef.current;
    setResendNote('');
    setErr('');
    try {
      const base = (getApiBase() || '').replace(/\/$/, '');
      const r = await fetch(`${base}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s?.token || ''}` },
      });
      const d = await r.json().catch(() => ({}));
      if (d.alreadyVerified) { setStep('plan'); return; }
      if (d.verifyCode) setDevCode(d.verifyCode);
      setCodeInput('');
      setResendNote(d.sent ? 'Sent — check your inbox.' : 'New code issued.');
    } catch (_) {
      setResendNote('Could not resend. Try again.');
    }
  }, []);

  // Tapping the email's verify BUTTON counts too — while the confirm step is
  // up, poll /me and hop forward the moment the account reads verified.
  useEffect(() => {
    if (step !== 'confirm') return;
    let stop = false;
    const s = pendingSessionRef.current;
    const base = (getApiBase() || '').replace(/\/$/, '');
    const t = setInterval(async () => {
      try {
        const r = await fetch(`${base}/api/auth/me`, { headers: { Authorization: `Bearer ${s?.token || ''}` } });
        const d = await r.json().catch(() => ({}));
        if (!stop && d && d.emailVerified) setStep('plan');
      } catch (_) { /* transient — keep polling */ }
    }, 3000);
    return () => { stop = true; clearInterval(t); };
  }, [step]);

  // ── Step 5: verifying — free gets a short "setting up" beat; paid polls the
  // backend until Stripe confirms the subscription is live, then unlocks.
  useEffect(() => {
    if (step !== 'verifying') return;
    let stop = false;
    const s = pendingSessionRef.current;
    const base = (getApiBase() || '').replace(/\/$/, '');
    const isPaid = selectedPlan === 'pro' || selectedPlan === 'ultra';
    const sleep = (ms) => new Promise((res) => setTimeout(res, ms));
    (async () => {
      if (!isPaid) {
        await sleep(1600);
        if (stop) return;
        setVerifyPhase('done');
        await sleep(1100);
        if (!stop) finalizeSignup();
        return;
      }
      const deadline = Date.now() + 45000;
      while (!stop && Date.now() < deadline) {
        try {
          const r = await fetch(`${base}/api/stripe/verify-activation`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s?.token || ''}` },
            body: JSON.stringify({ subscriptionId: payMeta?.subscriptionId || '' }),
          });
          const d = await r.json().catch(() => ({}));
          if (d.active) {
            if (stop) return;
            setVerifyNote(d.settling ? 'Bank transfers take a few days to settle — everything is unlocked in the meantime.' : '');
            setVerifyPhase('done');
            await sleep(1400);
            if (!stop) finalizeSignup();
            return;
          }
        } catch (_) { /* transient — keep polling */ }
        await sleep(1800);
      }
      if (!stop) setVerifyPhase('failed');
    })();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, verifyNonce]);

  // ── Step 2 screen: confirm email ────────────────────────────────────────────
  if (step === 'confirm') {
    const toEmail = pendingSessionRef.current?.email || emailInput;
    return createPortal(
      <div className="account-modal__backdrop">
        <div className="account-modal account-modal--signup" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="account-modal__close" onClick={onClose} aria-label="Close">×</button>
          <div className="account-modal__hero">
            <div className="account-modal__brand">HowTo</div>
            <h2 className="account-modal__title">Confirm your email</h2>
            <p className="account-modal__subtitle">
              We sent a 6-digit code to <strong>{toEmail}</strong>. Type it below — or tap the button in the email.
            </p>
          </div>
          <form className="account-modal__form" onSubmit={submitCode}>
            <input
              className="confirm-code"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="••••••"
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label="6-digit confirmation code"
              autoFocus
            />
            {devCode && (
              <p className="confirm-devnote">
                Email sending isn’t connected yet, so here’s your code: <strong>{devCode}</strong>
              </p>
            )}
            {err && <div className="account-modal__err">{err}</div>}
            <button type="submit" className="account-modal__submit" disabled={confirmBusy || codeInput.length !== 6}>
              {confirmBusy ? 'Checking…' : 'Confirm email'}
            </button>
            <div className="account-modal__toggle">
              Didn’t get it? <a onClick={(e) => { e.preventDefault(); resendCode(); }}>Resend code</a>
              {resendNote && <span className="confirm-resent">{resendNote}</span>}
            </div>
          </form>
        </div>
      </div>,
      document.body
    );
  }

  // ── Step 4 screen: payment info — the same embedded Stripe sheet the
  // Subscription page uses, kept INSIDE the wizard (close = back to plans).
  if (step === 'payment') {
    const s = pendingSessionRef.current || {};
    const isUltra = selectedPlan === 'ultra';
    return (
      <PaymentSheet
        plan={isUltra ? 'ultra' : 'subscription'}
        planLabel={isUltra ? 'HowTo Ultra' : 'HowTo Pro'}
        priceLabel={isUltra ? '$200/month' : '$20/month'}
        ctaLabel={`Start HowTo ${isUltra ? 'Ultra' : 'Pro'}`}
        userId={s.userId || ''}
        email={s.email || ''}
        onSuccess={(_res, meta) => {
          setPayMeta(meta || null);
          setVerifyPhase('checking');
          setVerifyNote('');
          setStep('verifying');
        }}
        onClose={() => setStep('plan')}
      />
    );
  }

  // ── Step 5 screen: verifying → unlocked ─────────────────────────────────────
  if (step === 'verifying') {
    const isPaid = selectedPlan === 'pro' || selectedPlan === 'ultra';
    const planName = selectedPlan === 'ultra' ? 'Ultra' : 'Pro';
    return createPortal(
      <div className="account-modal__backdrop">
        <div className="account-modal account-modal--signup verify-card" onClick={(e) => e.stopPropagation()}>
          {verifyPhase === 'checking' && (
            <div className="verify-state" role="status" aria-live="polite">
              <span className="account-modal__spinner verify-spinner" aria-hidden="true" />
              <h2 className="account-modal__title">{isPaid ? 'Verifying your payment…' : 'Setting up your workspace…'}</h2>
              <p className="account-modal__subtitle">
                {isPaid ? 'Confirming with your bank — this usually takes a few seconds.' : 'One moment while we get everything ready.'}
              </p>
            </div>
          )}
          {verifyPhase === 'done' && (
            <div className="verify-state" role="status" aria-live="polite">
              <div className="verify-check" aria-hidden="true">✓</div>
              <h2 className="account-modal__title">You’re in!</h2>
              <p className="account-modal__subtitle">
                {verifyNote || (isPaid ? `HowTo ${planName} is live — everything is unlocked.` : 'Your account is ready.')}
              </p>
            </div>
          )}
          {verifyPhase === 'failed' && (
            <div className="verify-state">
              <h2 className="account-modal__title">We couldn’t confirm the payment</h2>
              <p className="account-modal__subtitle">
                No worries — nothing was charged twice. Try again, or start on Free and add payment later in Settings → Subscription.
              </p>
              <button type="button" className="account-modal__submit" onClick={() => setStep('payment')}>
                Try payment again
              </button>
              <button
                type="button"
                className="overage-pop__alt"
                onClick={() => { setVerifyPhase('checking'); setVerifyNonce((n) => n + 1); }}
              >
                Check again
              </button>
              <button type="button" className="overage-pop__cancel" onClick={finalizeSignup}>
                Continue on Free for now
              </button>
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  }

  // ── Phone / SMS-consent step — between plan and payment. Number + the A2P/
  //    TCPA opt-in box (transactional, never marketing) with Terms + Privacy.
  if (step === 'phone') {
    return createPortal(
      <div className="account-modal__backdrop">
        <div className="account-modal account-modal--signup" onClick={(e) => e.stopPropagation()}>
          <div className="account-modal__brand">HowTo</div>
          <h2 className="account-modal__title">Text updates from Aria</h2>
          <p className="account-modal__subtitle">
            Optional — Aria can text you confirmations, reminders, and quick replies from HowTo's number. Add your mobile to turn it on, or skip for now. (Running a business? You'll set up your own business number with Aria on the Business plan.)
          </p>
          <input
            type="tel"
            className="account-modal__input"
            placeholder="(555) 123-4567"
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            autoComplete="tel"
            inputMode="tel"
          />
          <label style={{ display:'flex', gap:'10px', alignItems:'flex-start', margin:'12px 0 4px', textAlign:'left', fontSize:'13px', lineHeight:1.5, color:'#c9d6ee' }}>
            <input type="checkbox" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)} style={{ marginTop:'3px', flex:'0 0 auto' }} />
            <span>
              I agree to receive <b>service text messages</b> from HowTo (Aria) at this number.
              This texting is <b>part of the service — account and service messages only. We never send
              spam, promotions, or advertising.</b> Msg&nbsp;&amp;&nbsp;data rates may apply; reply STOP to opt out,
              HELP for help. See our <a href="/terms.html" target="_blank" rel="noopener">Terms</a> and{' '}
              <a href="/privacy.html" target="_blank" rel="noopener">Privacy Policy</a>.
            </span>
          </label>
          {err && <div className="account-modal__error">{err}</div>}
          <button type="button" className="account-modal__submit" disabled={phoneBusy} onClick={() => submitPhone({})}>
            {phoneBusy ? 'Saving…' : 'Turn on texts & continue'}
          </button>
          <button type="button" className="overage-pop__cancel" disabled={phoneBusy} onClick={() => submitPhone({ skip: true })}>
            Skip for now
          </button>
        </div>
      </div>,
      document.body
    );
  }

  // Plan choice — REQUIRED step after signup. Free and HowTo Pro side by side
  // (left → right), extra-usage toggle top right. Picking Pro with extra usage
  // ON pops a confirm first — the user must KNOW metered overage is enabled
  // before anything gets bought. Choice recorded server-side as consent:
  //   Free → conversations/data may be used to train + improve Aria
  //   Pro  → private; only what's legally required is kept (billing, security)
  if (step === 'plan') {
    const isPaid = selectedPlan === 'pro' || selectedPlan === 'ultra';
    const priceOf = { free: '$0', pro: '$20/mo', ultra: '$200/mo' };
    const confirmAndContinue = () => {
      if (isPaid && allowOverage) {
        // The user must SEE that extra usage is on before buying.
        setOverageConfirm(true);
        return;
      }
      choosePlan(selectedPlan);
    };
    return createPortal(
      <div className="account-modal__backdrop">
        <div
          className="plan-fullscreen"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Extra usage — TOP RIGHT, applies to any paid plan */}
          <label className={`plan-overage ${!isPaid ? 'plan-overage--off' : ''}`} title="Applies to paid plans">
            <span className="plan-overage__label">Extra usage</span>
            <span className={`aria-toggle ${allowOverage ? 'aria-toggle--on' : ''}`}>
              <input
                type="checkbox"
                checked={allowOverage}
                disabled={!isPaid}
                onChange={(e) => setAllowOverage(e.target.checked)}
              />
              <span className="aria-toggle__thumb" />
            </span>
          </label>

          <div className="plan-fullscreen__head">
            <div className="account-modal__brand">HowTo</div>
            <h2 className="plan-fullscreen__title">Pick how you want to run</h2>
            <p className="plan-fullscreen__sub">
              Email confirmed ✓ — now pick how you want to run. Change your plan any time in Settings.
            </p>
          </div>

          {/* Three columns, left → right: Free · Pro · Ultra.
              The cards themselves come from planCatalog, which the usage
              meter's "Get more usage" popup renders too — one copy of what a
              plan costs and promises, so the two surfaces cannot drift. The
              wizard keeps its own selection state, its own Continue (consent +
              choosePlan) and Ultra; only the card bodies are shared. */}
          <PlanColumns
            keys={['free', 'pro', 'ultra']}
            selected={selectedPlan}
            onSelect={setSelectedPlan}
          />

          <div className="plan-fullscreen__foot">
            <button
              type="button"
              className="account-modal__submit plan-fullscreen__cta"
              onClick={confirmAndContinue}
            >
              {selectedPlan === 'free' ? 'Continue with Free' : `Continue — HowTo ${selectedPlan === 'ultra' ? 'Ultra' : 'Pro'} · ${priceOf[selectedPlan]}`}
            </button>
            <p className="plan-fullscreen__terms">
              Free trains Aria on your data; paid plans are private. By continuing you agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a> &amp;{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </p>
          </div>

          {/* Confirm popup — buying a paid plan with extra usage ON */}
          {overageConfirm && (
            <div className="overage-pop__backdrop" onClick={() => setOverageConfirm(false)}>
              <div className="overage-pop" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-label="Extra usage is on">
                <h3>Heads up — extra usage is ON</h3>
                <p>
                  HowTo {selectedPlan === 'ultra' ? 'Ultra' : 'Pro'} includes <strong>{priceOf[selectedPlan]} of usage</strong>. With extra usage on, if you go past that,
                  Aria keeps working and you pay <strong>metered rates for what's used</strong>. Every charge shows
                  in Subscription → Usage.
                </p>
                <button
                  type="button"
                  className="account-modal__submit"
                  onClick={() => { setOverageConfirm(false); choosePlan(selectedPlan); }}
                >
                  Got it — keep extra usage on
                </button>
                <button
                  type="button"
                  className="overage-pop__alt"
                  onClick={() => { setAllowOverage(false); setOverageConfirm(false); choosePlan(selectedPlan); }}
                >
                  Turn it off — hard stop at {priceOf[selectedPlan]}
                </button>
                <button
                  type="button"
                  className="overage-pop__cancel"
                  onClick={() => setOverageConfirm(false)}
                >
                  Back
                </button>
              </div>
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="account-modal__backdrop" onClick={onClose}>
      <div
        className={`account-modal account-modal--${isSignup ? 'signup' : 'signin'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="account-modal__close"
          onClick={onClose}
          aria-label="Close"
        >×</button>

        {busy && (
          <div className="account-modal__loading" role="status" aria-live="polite">
            <span className="account-modal__spinner" aria-hidden="true" />
            <span className="account-modal__loading-text">{isSignup ? 'Creating your account…' : 'Signing you in…'}</span>
          </div>
        )}

        <div className="account-modal__hero">
          {isSignup ? (
            <>
              <div className="account-modal__brand">HowTo</div>
              <h2 className="account-modal__title">Create your account</h2>
              <p className="account-modal__subtitle">
                HowTo is your AI workspace — meet Aria, the agent who runs it for you. Your projects, voice, and data stay with you.
              </p>
            </>
          ) : (
            <>
              <div className="account-modal__brand account-modal__brand--small">HowTo</div>
              <h2 className="account-modal__title">Welcome back</h2>
              <p className="account-modal__subtitle">Sign in to pick up where you left off.</p>
            </>
          )}
        </div>

        <form className="account-modal__form" onSubmit={submit}>
          {!isSignup && savedLogin && (
            <div className="account-modal__saved">
              <button type="button" className="account-modal__submit account-modal__submit--saved" disabled={busy} onClick={signInSaved}>
                {busy ? '…' : `Sign in as ${savedLogin.email}`}
              </button>
              <a className="account-modal__forget" onClick={(e) => { e.preventDefault(); forgetSaved(); }}>Use a different account</a>
            </div>
          )}
          {isSignup && (
            <>
              <label className="account-modal__field">
                <span className="account-modal__label">Name</span>
                <input
                  type="text"
                  autoComplete="name"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder="First and last name"
                  maxLength={80}
                  required
                />
              </label>
              <div className="account-modal__field">
                <span className="account-modal__label">Birthday</span>
                <div className="bday-row">
                  {/* Typeable — you can type OR pick from the dropdown list. US order: Month · Day · Year. */}
                  <input className="bday-inp bday-inp--month" list="bday-months" value={bMonth} onChange={(e) => setBMonth(e.target.value)} placeholder="Month" autoComplete="off" aria-label="Month" />
                  <datalist id="bday-months">
                    {['January','February','March','April','May','June','July','August','September','October','November','December'].map((name) => <option key={name} value={name} />)}
                  </datalist>
                  <input className="bday-inp bday-inp--day" list="bday-days" value={bDay} onChange={(e) => setBDay(e.target.value)} placeholder="Day" inputMode="numeric" maxLength={2} autoComplete="off" aria-label="Day" />
                  <datalist id="bday-days">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d} />)}
                  </datalist>
                  <input className="bday-inp bday-inp--year" list="bday-years" value={bYear} onChange={(e) => setBYear(e.target.value)} placeholder="Year" inputMode="numeric" maxLength={4} autoComplete="off" aria-label="Year" />
                  <datalist id="bday-years">
                    {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i).map((y) => <option key={y} value={y} />)}
                  </datalist>
                </div>
              </div>
            </>
          )}
          <label className="account-modal__field">
            <span className="account-modal__label">Email</span>
            <input
              type="email"
              autoComplete="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="you@email.com"
              required
            />
          </label>
          <label className="account-modal__field">
            <span className="account-modal__label">Password</span>
            <input
              type="password"
              autoComplete={isSignup ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isSignup ? 'At least 8 characters' : 'Your password'}
              minLength={8}
              required
            />
          </label>

          {!isSignup && (
            <label className="account-modal__save">
              <input type="checkbox" checked={saveLogin} onChange={(e) => setSaveLogin(e.target.checked)} />
              <span>Save my login on this {isDesktopApp ? 'computer' : 'browser'}</span>
            </label>
          )}
          {savedNote && <div className="account-modal__note">{savedNote}</div>}

          {err && <div className="account-modal__err">{err}</div>}

          <button type="submit" className="account-modal__submit" disabled={busy}>
            {busy ? '…' : (isSignup ? 'Create my account' : 'Sign in')}
          </button>

          {isSignup && (
            <p className="account-modal__legal">
              By creating an account you agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              You must be 13+ (under 18 needs a parent or guardian's permission; payments and business tools require 18+).
            </p>
          )}

          <div className="account-modal__toggle">
            {isSignup ? (
              <>Already have an account? <a onClick={(e) => { e.preventDefault(); setMode('signin'); setErr(''); }}>Sign in</a></>
            ) : isDesktopApp ? (
              // Web-only signup: the desktop app is for members. Send new users
              // to the website to create an account.
              <>New here? Create your account at <a href="https://howtoaria.com" target="_blank" rel="noopener noreferrer">howtoaria.com</a></>
            ) : (
              <>New here? <a onClick={(e) => { e.preventDefault(); setMode('signup'); setErr(''); }}>Create an account</a></>
            )}
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function prettyErr(code, mode) {
  if (code === 'email_taken') return 'That email already has an account. Try signing in.';
  if (code === 'invalid_birthday') return 'That birthday doesn’t look right.';
  if (code === 'age_requirement') return 'You must be at least 13 to create an account.';
  if (code === 'invalid_credentials') return 'Email or password is wrong.';
  if (code === 'locked') return 'Too many tries. Wait a moment.';
  if (code === 'missing_credentials') return 'Enter both email and password.';
  if (/password/i.test(code)) return 'Password must be at least 8 characters.';
  return mode === 'signup' ? `Sign up failed (${code}).` : `Sign in failed (${code}).`;
}
